import { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import { getContextFromFeaturePrd } from "@/components/AI/EngSpec/utils";
import {
  PRDDesignSuggestion,
  PRDDesignSuggestions,
} from "@/components/AI/PRD/PRD";
import { createDummyComponent } from "@/components/AppHeader/utils";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSaveFile } from "@/hooks/useSaveFile";
import { setIFrameChanged, setSelectedElementKey } from "@/store/iframeSlice";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import {
  ManualAssistantMessage,
  useAiContextStore,
} from "@/store/zustand/aiContextStore";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { generateJsonStream } from "@/utils/ai/utils";
import { EMPTY_TREE_CODEBASE_ID, STORYBOARD_TYPES } from "@/utils/constants";
import {
  useLoadCodebase,
  useStartServer,
  useRunTerminalCommand,
} from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import { ReactFramework } from "@/utils/templateProjects";
import { TempoElement } from "@/utils/tempoElement";
import { Storyboard } from "@/utils/types";
import {
  fixJson,
  getUnexpiredToken,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";

export const useTriggerGenerateSb = () => {
  const canvasId = useCanvasId();

  const generatingStoryboardIds = useAiContextStore(
    (state) => state.generatingStoryboardIds
  );
  const setGeneratingStoryboardIds = useAiContextStore(
    (state) => state.setGeneratingStoryboardIds
  );

  const mainCanvas = useActiveMainCanvas();
  const mainCanvasRef = useRef(mainCanvas);

  useEffect(() => {
    mainCanvasRef.current = mainCanvas;
  }, [mainCanvas]);

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree
  );

  const storyboardToLoaded = useMemo(() => {
    const storyboardToLoaded = {};
    Object.keys(storyboardToNavTree || {}).forEach((storyboardId) => {
      if (
        storyboardToNavTree?.[storyboardId] &&
        storyboardToNavTree?.[storyboardId]?.tempoElement.codebaseId &&
        storyboardToNavTree?.[storyboardId]?.tempoElement.codebaseId !==
          EMPTY_TREE_CODEBASE_ID
      ) {
        storyboardToLoaded[storyboardId] = true;
      }
    });
    return storyboardToLoaded;
  }, [storyboardToNavTree]);

  const storyboardToLoadedRef = useRef(storyboardToLoaded);
  useEffect(() => {
    storyboardToLoadedRef.current = storyboardToLoaded;
  }, [storyboardToLoaded]);

  const project = useActiveProject();

  const saveFile = useSaveFile();

  const { supabaseClient } = useSupabase();
  const dispatch = useDispatch();

  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const { session } = useSession();
  const [abortController, setAbortController] = useState(new AbortController());
  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);

  const runRagFF = useFeatureFlagEnabled("run-rag-generate-wireframes");

  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles
  );

  const startServer = useStartServer();
  const runTerminalCommand = useRunTerminalCommand();
  const loadCodebase = useLoadCodebase();

  useEffect(() => {
    if (stopAiTrigger && abortController) {
      abortController.abort();
      // @ts-expect-error TS2345
      setAbortController(null);
    }
  }, [stopAiTrigger]);

  const triggerGenerate = async (onlyMissing?: boolean) => {
    if (generatingStoryboardIds?.size) {
      return;
    }

    // The message from the design agent component, just add progress to it
    const previousAssistantMessage =
      useAiContextStore.getState().tempAssistantMessage;
    const newAssistantMessage: ManualAssistantMessage = {
      ...previousAssistantMessage,
      progressItems: [
        ...(previousAssistantMessage?.progressItems || []),
        {
          title: "Creating Routes",
          loading: true,
        },
      ],
    };
    setTempAssistantMessage(newAssistantMessage);

    try {
      // Fetch the design suggestion, fetch it fresh here so that it doesn't cause re-renders
      const featurePrds = useSupabaseStore.getState().featurePrds;
      const featurePrd = Object.values(featurePrds).find(
        (prd) => prd.canvas_id === canvasId
      );

      const designSuggestions =
        featurePrd?.design_suggestions as any as PRDDesignSuggestions;

      const levelToSuggestionsToGenerate = {};
      const allPaths = new Set<string>();
      const allStoryboardIds = new Set<string>();

      /**
       * Adds the suggestions in a bottom-up order
       */
      const addSuggestionToGenerate = (
        suggestion: PRDDesignSuggestion,
        level: number
      ) => {
        suggestion.components?.forEach((s) => {
          addSuggestionToGenerate(s, level + 1);
        });

        allPaths.add(suggestion.path);
        allStoryboardIds.add(suggestion.id);

        levelToSuggestionsToGenerate[level] =
          levelToSuggestionsToGenerate[level] || [];
        levelToSuggestionsToGenerate[level].push(suggestion);
      };

      addSuggestionToGenerate(designSuggestions.pageOrComponent, 1);

      setGeneratingStoryboardIds(allStoryboardIds);

      const fileToContents = {};
      Array.from(allPaths).map((filePath) => {
        let contents = "<div/>";
        if (project?.framework === ReactFramework.EXPO) {
          contents = "<View/>";
        }

        fileToContents[filePath] = createDummyComponent(
          contents,
          project.framework
        );
      });
      await saveFile({
        fileToContentsToSave: fileToContents,
        filenameToFileKey: undefined,
        saveActivityType: ActivityChangeType.AI_CREATE_WIREFRAME,
        onlyCreate: true,
      });

      // @ts-expect-error TS18048
      newAssistantMessage.progressItems[
        // @ts-expect-error TS18048
        newAssistantMessage.progressItems.length - 1
      ].succeeded = true;
      setTempAssistantMessage({ ...newAssistantMessage });

      // Now process

      const idToCreatedFile = {};
      const idToStoryboard = {};

      const markStoryboardsAsGenerating = async (
        matchingSuggestions: PRDDesignSuggestion[]
      ): Promise<string[]> => {
        const allIds = new Set(matchingSuggestions.map((s) => s.id));
        const storyboards = mainCanvasRef.current.storyboards?.filter((sb) =>
          allIds.has(sb.id)
        );

        if (!storyboards?.length) {
          return [];
        }

        let scolled = false;
        const newStoryboards = mainCanvasRef.current.storyboards.map((sb) => {
          if (allIds.has(sb.id)) {
            let newSb = {
              ...sb,
              aiGenerating: true,
            };
            idToStoryboard[sb.id] = newSb;

            if (!scolled) {
              newSb = {
                ...newSb,
                scrollToStoryboard: true,
              };

              scolled = true;
            }

            return newSb;
          } else if (idToStoryboard[sb.id]) {
            return idToStoryboard[sb.id];
          }

          return sb;
        });

        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          newStoryboards,
          mainCanvasRef.current
        );

        return storyboards.map((sb) => sb.id);
      };

      const createFileAndAttachToStoryboards = async (
        matchingSuggestions: PRDDesignSuggestion[],

        filePath: string,
        fileContents: string
      ) => {
        const allIds = new Set(matchingSuggestions.map((s) => s.id));
        const storyboards = mainCanvasRef.current.storyboards?.filter((sb) =>
          allIds.has(sb.id)
        );

        if (!storyboards?.length) {
          return;
        }

        if (!filePath || !fileContents) {
          // Invalid response, reset storyboard
          const newStoryboards = mainCanvasRef.current.storyboards.map((sb) => {
            if (allIds.has(sb.id)) {
              const newSb = {
                ...sb,
                aiGenerating: false,
              };
              idToStoryboard[sb.id] = newSb;
              return newSb;
            } else if (idToStoryboard[sb.id]) {
              return idToStoryboard[sb.id];
            }

            return sb;
          });

          await updateCanvasStoryboards(
            supabaseClient,
            dispatch,
            newStoryboards,
            mainCanvasRef.current
          );
          return;
        }

        storyboards.forEach((sb) => {
          idToCreatedFile[sb.id] = true;
        });

        const res: any = await saveFile({
          fileToContentsToSave: { [filePath]: fileContents },
          saveActivityType: ActivityChangeType.AI_CREATE_WIREFRAME,
        });

        // If replacing the main application storyboard, select it to reload it
        if (storyboards[0].type === STORYBOARD_TYPES.APPLICATION) {
          dispatch(
            setSelectedElementKey(TempoElement.forStoryboard(storyboards[0].id))
          );
        }

        const fileModified = res.data?.changedFiles?.[0];

        const createdComponent: any = Object.values(
          res.data.treeElementLookup || {}
        ).find(
          (treeElement: any) =>
            treeElement.type === "component" &&
            treeElement.filename?.endsWith(matchingSuggestions[0].path)
        );

        const newFullFilePath =
          createdComponent?.filename || fileModified || filePath;
        const savedCodebaseId = createdComponent?.codebaseId;

        const idToNewSb = {};
        storyboards.forEach((sb) => {
          const newSb: Storyboard = {
            ...sb,
            created: true,
            created_at: new Date().toISOString(),
            saved: true,
            saving: false,
            recentlySavedTs: new Date().toISOString(),
            fullFilePath: newFullFilePath,
            savedCodebaseId,
            storyboardComponentCodebaseId: savedCodebaseId,
            scrollToStoryboard: true,
            aiGenerating: false,
            isPlaceholder: false,
          };

          idToNewSb[sb.id] = newSb;
          idToStoryboard[sb.id] = {
            ...newSb,

            // If setting this state back later, do not re-scroll
            scrollToStoryboard: false,
          };
        });

        const newStoryboards = mainCanvasRef.current.storyboards.map((sb) => {
          if (idToNewSb[sb.id]) {
            return idToNewSb[sb.id];
          }

          if (idToStoryboard[sb.id]) {
            return idToStoryboard[sb.id];
          }

          return sb;
        });

        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          newStoryboards,
          mainCanvasRef.current
        );
      };

      const pathGenerated = {};
      const batches = [];

      Object.keys(levelToSuggestionsToGenerate)
        .sort()
        .reverse()
        .forEach((level) => {
          const suggestionsToGenerateInOrder =
            levelToSuggestionsToGenerate[level];

          const inputsForBatch = [];
          for (let i = 0; i < suggestionsToGenerateInOrder.length; i++) {
            const suggestion = suggestionsToGenerateInOrder[i];
            if (pathGenerated[suggestion.path]) {
              continue;
            }

            pathGenerated[suggestion.path] = true;

            // Multiple components can match (previously defined ones)
            const matchingComponents = suggestionsToGenerateInOrder.filter(
              (s) => s.path === suggestion.path
            );

            const allIds = new Set(matchingComponents.map((s) => s.id));
            const validStoryboards = mainCanvasRef.current.storyboards?.filter(
              (sb) => allIds.has(sb.id)
            );

            if (!validStoryboards?.length) {
              continue;
            }

            let name = validStoryboards[0].name;
            if (validStoryboards[0].type === STORYBOARD_TYPES.APPLICATION) {
              name = "Home Route";
            }

            const newProgressItem = {
              title: `Generating ${name}`,
            };
            // @ts-expect-error TS18048
            newAssistantMessage.progressItems.push(newProgressItem);
            setTempAssistantMessage({ ...newAssistantMessage });

            // @ts-expect-error TS2345
            inputsForBatch.push({
              suggestion,
              matchingComponents,
              progressItem: newProgressItem,
            });
          }

          // @ts-expect-error TS2345
          batches.push(inputsForBatch);
        });

      let restartedOnce = false;
      for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
        const inputsForBatch = batches[batchIdx];

        // Mark all progress items as loading
        // @ts-expect-error TS2339
        inputsForBatch.forEach((input) => {
          input.progressItem.loading = true;
        });
        setTempAssistantMessage({ ...newAssistantMessage });

        // Get all matching components and mark storyboards as generating
        const allMatchingComponents = inputsForBatch
          // @ts-expect-error TS2339
          .map((input) => input.matchingComponents)
          .flat();

        const storyboardIdsInBatch = await markStoryboardsAsGenerating(
          allMatchingComponents
        );

        const allInstallCommands = [];
        const allSaveFileFnsAfterInstall = [];

        const supabaseJWT = await getUnexpiredToken(session);

        await Promise.all(
          // @ts-expect-error TS2339
          inputsForBatch.map(async (input) => {
            const { suggestion, matchingComponents } = input;

            const url = `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/canvases/${canvasId}/ai/vercel-ai/aiWireframesGenerateCode`;

            const { contextWithImages, userSelectionContext } =
              getContextFromFeaturePrd(featurePrd);

            const generateImages =
              generateAttachedFiles && generateAttachedFiles.length > 0
                ? generateAttachedFiles.map((file) => ({
                    imageUrl: file.url,
                    context: "Image provided as part of the original prompt",
                  }))
                : [];

            const allVisualContext = [...contextWithImages, ...generateImages];

            const treeElementLookup =
              useCodebaseStore.getState().treeElementLookup;
            const componentLibrary = Object.values(treeElementLookup)
              .map((e) => ({
                type: e.type,
                filename: e.filename,
                componentName: e.componentName,
                defaultExport: e.defaultExport,
              }))
              .filter((e) => e.type === "component");

            const body = {
              feature_title: featurePrd?.title || "",
              feature_prd: featurePrd?.prd || "",
              visual_context: allVisualContext,
              user_selection_context: JSON.stringify(userSelectionContext),
              mermaid_diagram: featurePrd?.mermaid_diagram || "",
              ui_design_scaffolding_spec: JSON.stringify(designSuggestions),
              specific_file: suggestion.path,
              filenames: Array.from(
                Object.keys(pathGenerated).filter((p) => p !== suggestion.path)
              ),
              component_library: JSON.stringify(componentLibrary),
              runRag: runRagFF,
              original_user_prompt: generateText,
            };

            const newAbortController = new AbortController();
            setAbortController(newAbortController);

            const stream = await generateJsonStream(url, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(body),
              signal: newAbortController.signal,
            });

            let res = null;

            for await (let chunk of stream) {
              if (chunk.status === -1 && chunk.message === "intermediate") {
                // Right now we don't care about the streamed in data
              } else {
                res = chunk;
                break;
              }
            }

            // @ts-expect-error TS18047
            if (res.status >= 400) {
              console.error({
                // @ts-expect-error TS18047
                msg: res.message,
                // @ts-expect-error TS18047
                internalErrorStr: res.internalErrorStr,
              });
              // @ts-expect-error TS18047
              throw new Error(res.message);
            }

            // Notify the iframe changed
            dispatch(setIFrameChanged());

            // @ts-expect-error TS18047
            const finalText = res.data.payload;
            const resJson = fixJson(finalText).fixedObj;

            const saveFileAndMarkFinished = async () => {
              await createFileAndAttachToStoryboards(
                matchingComponents,
                suggestion.path,
                resJson?.fileContents
              );

              // Mark progress item as succeeded
              input.progressItem.succeeded = true;
              setTempAssistantMessage({ ...newAssistantMessage });

              refreshPlanInfo(session);
            };

            if (resJson?.installCommand) {
              // @ts-expect-error TS2345
              allInstallCommands.push(resJson.installCommand);
              // @ts-expect-error TS2345
              allSaveFileFnsAfterInstall.push(saveFileAndMarkFinished);
            } else {
              await saveFileAndMarkFinished();
            }
          })
        );

        if (allInstallCommands.length) {
          const combinedCommand = allInstallCommands.join(" && ");

          const newProgressItem: any = {
            title: `Installing Dependencies`,
            loading: true,
          };
          // @ts-expect-error TS18048
          newAssistantMessage.progressItems.push(newProgressItem);
          setTempAssistantMessage({ ...newAssistantMessage });

          const { success, error } = await runTerminalCommand({
            command: combinedCommand,
            abortController,
          });

          if (abortController?.signal.aborted) {
            throw new Error("Operation aborted by user");
          }

          if (!success) {
            throw new Error(error || "Unknown error");
          }

          // Reload the codebase to detect changes to the codebase (package.json)
          loadCodebase(false, false);

          newProgressItem.succeeded = true;
          setTempAssistantMessage({ ...newAssistantMessage });

          // Finally save the files after the install (so no errors are thrown)
          // @ts-expect-error TS2349
          await Promise.all(allSaveFileFnsAfterInstall.map((fn) => fn()));

          restartedOnce = true;
        } else if (!restartedOnce) {
          // Wait 3 seconds for the storyboard to load, and if it has not loaded, restart the server
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const anyStoryboardLoaded = storyboardIdsInBatch.some(
            (sbId) => storyboardToLoadedRef.current[sbId]
          );

          if (!anyStoryboardLoaded && !restartedOnce) {
            await startServer({
              forceRestart: true,
            });

            restartedOnce = true;
          }
        }

        // Mark all progress items as succeeded
        // @ts-expect-error TS2339
        inputsForBatch.forEach((input) => {
          input.progressItem.succeeded = true;
        });
        setTempAssistantMessage({ ...newAssistantMessage });
      }

      toast.success("Generated");
      // @ts-expect-error TS2345
      setGeneratingStoryboardIds(null);

      // @ts-expect-error TS2345
      setAbortController(null);

      // @ts-expect-error TS18048
      newAssistantMessage.progressItems[
        // @ts-expect-error TS18048
        newAssistantMessage.progressItems.length - 1
      ].succeeded = true;
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage(newAssistantMessage);

      track("FUTE_SUCCESS", {
        canvas_id: canvasId,
      });
    } catch (err) {
      logAxiosError("Error generating storyboard code", err);

      if (
        err.name !== "AbortError" &&
        err.message !== "Operation aborted by user"
      ) {
        toast.error(`Error generating storyboard code: ${err.message}`);
      }
      // @ts-expect-error TS2345
      setGeneratingStoryboardIds(null);

      // @ts-expect-error TS2345
      setAbortController(null);

      // Mark all incomplete items as failed
      // @ts-expect-error TS18048
      newAssistantMessage.progressItems.forEach((item) => {
        if (!item.succeeded) {
          item.failed = true;
        }
      });
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage(newAssistantMessage);
    } finally {
      refreshPlanInfo(session);
    }
  };

  return { triggerGenerate };
};

export const getPreviouslyDefinedSuggestion = (
  designSuggestions: PRDDesignSuggestions,
  designSuggestion: PRDDesignSuggestion
) => {
  if (!designSuggestion.previouslyDefined) {
    return designSuggestion;
  }

  const toSearch: PRDDesignSuggestion[] = [designSuggestions.pageOrComponent];

  while (toSearch.length) {
    let obj = toSearch.pop();
    // @ts-expect-error TS18048
    if (!obj.previouslyDefined && obj.path === designSuggestion.path) {
      return obj;
    }

    // @ts-expect-error TS18048
    toSearch.push(...(obj.components || []));
  }

  return null;
};

export const getDesignSuggestionForSbId = (
  designSuggestions: PRDDesignSuggestions,
  sbId: string
) => {
  if (!designSuggestions?.pageOrComponent) {
    return null;
  }

  const toSearch: PRDDesignSuggestion[] = [designSuggestions?.pageOrComponent];
  const pathToSuggestion = {};
  let searchForPath = null;
  while (toSearch.length) {
    let obj = toSearch.pop();
    // @ts-expect-error TS18048
    if (obj.previouslyDefined) {
      // @ts-expect-error TS18048
      if (obj.id !== sbId) {
        continue;
      }

      // @ts-expect-error TS18048
      const searchObj = pathToSuggestion[obj.path];
      if (searchObj) {
        return searchObj;
      }

      // Search for the object by path this time
      // @ts-expect-error TS2322
      searchForPath = obj.path;
      continue;
    }

    // @ts-expect-error TS18048
    pathToSuggestion[obj.path] = obj;

    // The storyboard for a design suggestion always has the same ID as the suggestion
    // @ts-expect-error TS18048
    if (obj.id === sbId || (searchForPath && obj.path === searchForPath)) {
      return obj;
    }
    // @ts-expect-error TS18048
    toSearch.push(...(obj.components || []));
  }

  return null;
};
